import { CanActivate, ExecutionContext, ForbiddenException, Injectable, UnauthorizedException } from '@nestjs/common';
import { Request } from 'express';
import { Application } from '@chat/prisma-client';
import { PrismaService } from '../../prisma/prisma.service';
import { hashApiKey } from '../crypto/api-key.util';

export interface ApplicationScopedRequest extends Request {
  application: Application;
}

@Injectable()
export class ApiKeyGuard implements CanActivate {
  constructor(private readonly prisma: PrismaService) {}

  async canActivate(context: ExecutionContext): Promise<boolean> {
    const request = context.switchToHttp().getRequest<ApplicationScopedRequest>();
    const rawKey = this.extractKey(request);
    const keyHash = hashApiKey(rawKey);

    const apiKey = await this.prisma.apiKey.findUnique({
      where: { keyHash },
      include: { application: true },
    });

    if (!apiKey || apiKey.revokedAt) {
      throw new UnauthorizedException('Invalid or revoked API key');
    }

    const { application } = apiKey;
    if (application.status !== 'active' || !application.isActive || application.deletedAt) {
      throw new UnauthorizedException('Application is not active');
    }

    this.checkOrigin(request, application);

    request.application = application;

    // Best-effort recency tracking — never block or fail the request over it.
    this.prisma.apiKey
      .update({ where: { id: apiKey.id }, data: { lastUsedAt: new Date() } })
      .catch(() => undefined);

    return true;
  }

  private extractKey(request: Request): string {
    const [scheme, token] = request.headers.authorization?.split(' ') ?? [];

    if (scheme !== 'Bearer' || !token) {
      throw new UnauthorizedException('Authorization bearer API key is required');
    }

    return token;
  }

  private checkOrigin(request: Request, application: Application): void {
    const origin = request.headers.origin;

    // Only browser-originated requests carry an Origin header — server-to-
    // server calls (e.g. an n8n workflow) legitimately have none, so this
    // check only applies when the header is actually present.
    if (!origin) return;

    if (!application.allowedOrigins.includes(origin)) {
      throw new ForbiddenException(`Origin "${origin}" is not allowed for this application`);
    }
  }
}
